import React, { useEffect, useState, useCallback } from "react";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { Container, Row, Col, Card, Form, Button, Modal, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaChartBar } from "react-icons/fa";
import { useComedores } from '../../services/hooks/useComedores';
import { useClientes } from '../../services/hooks/useClientes';
import { reporteApi } from '../../services/api/reporteApi';
import "react-toastify/dist/ReactToastify.css";

// Función auxiliar fuera del componente para evitar problemas de dependencias
const formatDate = (date) => {
  return date.toISOString().split("T")[0];
};

const ReporteAtencionComedor = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Usar los hooks para las operaciones de API
  const { comedores, fetchComedoresByProyecto, loading: loadingComedores } = useComedores();
  const { clientes, fetchClientesByProyecto, loading: loadingClientes } = useClientes();
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    id_cliente: "",
    id_proyecto: selectedProject ? selectedProject.id : "",
    id_comedor: "",
    tipo_reporte: "completo",
    fechaDesde: formatDate(new Date()),
    fechaHasta: formatDate(new Date()),
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Reporte de Atención por Comedor | SGC";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Cargar comedores y clientes vinculados al proyecto seleccionado
  useEffect(() => {
    if (selectedProject?.id) {
      fetchData();
    }
  }, [selectedProject?.id]);

  const fetchData = useCallback(async () => {
    try {
      await Promise.all([
        fetchComedoresByProyecto(selectedProject.id),
        fetchClientesByProyecto(selectedProject.id),
      ]);
    } catch (error) {
      console.error("Error al cargar los datos:", error);
      toast.error("Error al cargar los datos iniciales");
    }
  }, [selectedProject?.id, fetchComedoresByProyecto, fetchClientesByProyecto]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "tipo_reporte") {
      setFormData({ ...formData, tipo_reporte: value });
      return;
    }
    setFormData({
      ...formData,
      [name]: name.includes("fecha")
        ? value
        : value.trim() === ""
        ? ""
        : value === "Todos"
        ? "Todos"
        : parseInt(value),
    });
  };

  const applyStylesToWorkbook = (workbook) => {
    const ws = workbook.Sheets[workbook.SheetNames[0]];

    // Definir estilos para los encabezados
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: {
        patternType: "solid",
        fgColor: { rgb: "FF4472C4" }, // Azul más profesional
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true
      },
      border: {
        top: { style: "thin", color: { rgb: "FF000000" } },
        bottom: { style: "thin", color: { rgb: "FF000000" } },
        left: { style: "thin", color: { rgb: "FF000000" } },
        right: { style: "thin", color: { rgb: "FF000000" } },
      },
    };

    // Estilo para las celdas de datos
    const dataStyle = {
      alignment: {
        horizontal: "left",
        vertical: "center",
        wrapText: true
      },
      border: {
        top: { style: "thin", color: { rgb: "FFD3D3D3" } },
        bottom: { style: "thin", color: { rgb: "FFD3D3D3" } },
        left: { style: "thin", color: { rgb: "FFD3D3D3" } },
        right: { style: "thin", color: { rgb: "FFD3D3D3" } },
      },
    };

    // Aplicar estilos a las celdas
    for (const col in ws) {
      if (col[0] === "!") continue;
      
      // Aplicar estilo de encabezado a la primera fila
      if (col.match(/[A-Z]+1$/)) {
        if (!ws[col].s) {
          ws[col].s = {};
        }
        Object.assign(ws[col].s, headerStyle);
      } else {
        // Aplicar estilo de datos a las demás filas
        if (!ws[col].s) {
          ws[col].s = {};
        }
        Object.assign(ws[col].s, dataStyle);
      }
    }

    // Ajustar el ancho de las columnas para texto completo
    ws["!cols"] = [
      { wch: 25 }, // Comedor
      { wch: 25 }, // Cliente
      { wch: 15 }, // N° Documento
      { wch: 30 }, // Nombre Y Apellido
      { wch: 20 }, // Tipo de Cargo
      { wch: 25 }, // Empresa
      { wch: 20 }, // Referencia
      { wch: 15 }, // Fecha Servicio
      { wch: 20 }, // Tipo Servicio
    ];

    // Establecer altura de las filas para permitir texto ajustado
    if (!ws["!rows"]) ws["!rows"] = [];
    // Altura para encabezados
    ws["!rows"][0] = { hpt: 30 };
  };

  const exportToCSV = (csvData, fileName) => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    applyStylesToWorkbook(wb);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  };

  const transformDataForExport = (reporteData) => {
    return reporteData.map(item => ({
      "Comedor": item.comedor_nombre,
      "Cliente": item.cliente_nombre,
      "N° Documento": item.numero_documento,
      "Nombre Y Apellido": item.nombre_apellido,
      "Tipo de Cargo": item.tipo_cargo,
      "Empresa": item.empresa,
      "Referencia": item.referencia,
      "Fecha Servicio": item.fecha_servicio,
      "Tipo Servicio": item.tipo_servicio,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.id_cliente) {
      toast.error("Por favor seleccione un cliente");
      return;
    }

    if (!formData.id_comedor) {
      toast.error("Por favor seleccione un comedor");
      return;
    }

    if (!formData.fechaDesde || !formData.fechaHasta) {
      toast.error("Por favor seleccione las fechas");
      return;
    }

    const fechaDesdeDate = new Date(formData.fechaDesde);
    const fechaHastaDate = new Date(formData.fechaHasta);

    if (fechaDesdeDate > fechaHastaDate) {
      toast.error("La fecha desde no puede ser mayor a la fecha hasta");
      return;
    }

    setIsGenerating(true);

    try {
      const response = await reporteApi.descargarReporteAtencionPorComedorExcel({
        id_proyecto: formData.id_proyecto,
        id_cliente: formData.id_cliente,
        id_comedor: formData.id_comedor,
        fecha_desde: formData.fechaDesde,
        fecha_hasta: formData.fechaHasta,
        tipo_reporte: formData.tipo_reporte,
      });

      // El backend devuelve directamente el archivo Excel
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // El nombre del archivo viene del backend en los headers, pero por si acaso...
      const nombreProyecto = selectedProject?.nombre || 'Proyecto';
      const fechaDesdeFormateada = formData.fechaDesde.replace(/-/g, '');
      const fechaHastaFormateada = formData.fechaHasta.replace(/-/g, '');
      const esListado = formData.tipo_reporte === "listado";
      const nombreArchivo = esListado
        ? `Proyecto_${nombreProyecto}_Reporte_Consumos_Listado_${fechaDesdeFormateada}_${fechaHastaFormateada}.xlsx`
        : `Proyecto_${nombreProyecto}_Reporte_Atenciones_Comedor_${fechaDesdeFormateada}_${fechaHastaFormateada}.xlsx`;
      
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Reporte Excel generado y descargado exitosamente");
    } catch (error) {
      console.error("Error generando reporte:", error);
      if (error.response?.status === 404) {
        toast.warning("No se encontraron atenciones para los filtros seleccionados");
      } else {
        toast.error(error.response?.data?.message || "Error al generar el reporte");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      {/* Digital Harmony Styles para Reportes */}
      <style>
        {`
          /* Fondo degradado */
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            background: white;
          }
          
          .dh-form-label {
            font-weight: 600;
            color: #374151;
            margin-bottom: 0.5rem;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />

        <Modal
          show={isGenerating}
          backdrop="static"
          keyboard={false}
          centered
          aria-labelledby="reporte-atencion-comedor-loading-title"
        >
          <Modal.Body className="text-center py-5 px-4">
            <div className="d-flex justify-content-center mb-3">
              <Spinner
                animation="border"
                role="status"
                variant="primary"
                style={{ width: "3.5rem", height: "3.5rem" }}
              />
            </div>
            <FaFileExcel className="text-success mb-3" style={{ fontSize: "2rem" }} aria-hidden />
            <h5 id="reporte-atencion-comedor-loading-title" className="mb-2">
              Generando el reporte Excel
            </h5>
            <p className="text-muted mb-0 small">
              El servidor está preparando el archivo de atenciones por comedor. No cierre esta ventana;
              con muchos registros puede tardar varios minutos.
            </p>
          </Modal.Body>
        </Modal>
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaChartBar className="me-3" />
                  Reporte de Atención Por Comedor
                </h1>
                <p className="dh-main-subtitle">
                  Generación y exportación — formato completo o listado de consumos
                </p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <h5 className="mb-0">
                    <FaFileExcel className="me-2" />
                    Configuración del Reporte
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Proyecto</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedProject?.nombre || ''}
                            disabled
                            className="dh-form-control"
                            style={{ background: '#f8f9fa', color: '#6b7280' }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Tipo de reporte *</Form.Label>
                          <Form.Select
                            name="tipo_reporte"
                            value={formData.tipo_reporte}
                            onChange={handleInputChange}
                            required
                            className="dh-form-control"
                          >
                            <option value="completo">
                              Formato completo — comedor, cliente, documento, fechas, referencia y servicio
                            </option>
                            <option value="listado">
                              Listado de consumos — N°, DNI, nombres, cargo, empresa, fecha, servicio, valor, comedor
                            </option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            {formData.tipo_reporte === "listado"
                              ? "Exporta una hoja LISTADO con una fila por cada consumo/atención."
                              : "Exporta el reporte completo de atenciones por comedor (formato actual)."}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Cliente *</Form.Label>
                          <Form.Select
                            name="id_cliente"
                            value={formData.id_cliente}
                            onChange={handleInputChange}
                            required
                            disabled={loadingClientes}
                            className="dh-form-control"
                          >
                            <option value="">
                              {loadingClientes ? 'Cargando clientes...' : 'Selecciona un cliente'}
                            </option>
                            <option value="Todos" style={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>
                              🏢 Todos los Clientes
                            </option>
                            {clientes?.map((cliente) => (
                              <option key={cliente.id} value={cliente.id}>
                                {cliente.nombre || cliente.nombre_sociedad}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Comedor *</Form.Label>
                          <Form.Select
                            name="id_comedor"
                            value={formData.id_comedor}
                            onChange={handleInputChange}
                            required
                            disabled={loadingComedores}
                            className="dh-form-control"
                          >
                            <option value="">
                              {loadingComedores ? 'Cargando comedores...' : 'Selecciona un comedor'}
                            </option>
                            <option value="Todos" style={{ fontWeight: 'bold', backgroundColor: '#e8f5e8' }}>
                              🍽️ Todos los Comedores
                            </option>
                            {comedores?.map((comedor) => (
                              <option key={comedor.id} value={comedor.id}>
                                {comedor.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Fecha Desde *</Form.Label>
                          <Form.Control
                            type="date"
                            name="fechaDesde"
                            value={formData.fechaDesde}
                            onChange={handleInputChange}
                            required
                            className="dh-form-control"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Fecha Hasta *</Form.Label>
                          <Form.Control
                            type="date"
                            name="fechaHasta"
                            value={formData.fechaHasta}
                            onChange={handleInputChange}
                            required
                            className="dh-form-control"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-grid">
                      <Button 
                        type="submit"
                        disabled={isGenerating}
                        className="btn-dh-primary d-flex align-items-center justify-content-center"
                        size="lg"
                      >
                        {isGenerating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2 text-white" role="status" />
                            Generando reporte…
                          </>
                        ) : (
                          <>
                            <FaDownload className="me-2" />
                            Generar Reporte Excel
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteAtencionComedor;
